import React, { useEffect, useState } from "react";
import {
  Autocomplete,
  TextField,
  Box,
  Grid2,
  Typography,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import axios from "axios";
import CURRENCIES from "../../../../constants/currencies";

const Step2 = ({ rfq, setRfq, setStep }) => {
  const origins = ["New York", "London", "Mumbai", "Shanghai", "Hamburg"];
  const destinations = ["Tokyo", "Dubai", "Singapore", "Sydney", "Rotterdam"];
  const defaultCurrency = { name: "INR", rate: 1 };

  const [selectedCurr, setSelectedCurr] = useState(null);
  const [selectedRate, setSelectedRate] = useState("");
  const [btnLoading, setBtnLoading] = useState(false);

  const storedUser = JSON.parse(localStorage.getItem("user")) || {};

  const getCurrencyArray = () => {
    if (Array.isArray(rfq.currencies)) return rfq.currencies;
    if (typeof rfq.currencies === "string") {
      try { return JSON.parse(rfq.currencies); } catch { return []; }
    }
    return [];
  };

  const currencyArray = getCurrencyArray();

  useEffect(() => {
    if (currencyArray.length === 0) {
      setRfq((prev) => ({
        ...prev,
        currencies: [defaultCurrency],
      }));
    }
  }, [currencyArray.length, setRfq]);

  const addCurrencyRate = () => {
    if (!selectedCurr || !selectedRate) return;
    const exists = currencyArray.some(c => c.name === selectedCurr);
    if (exists) {
      alert("This currency has already been added.");
      return;
    }
    const newEntry = { name: selectedCurr, rate: selectedRate };
    setRfq((prev) => ({
      ...prev,
      currencies: [...currencyArray, newEntry]
    }));
    setSelectedCurr(null);
    setSelectedRate("");
  };

  const removeCurrencyRate = (index) => {
    const updated = currencyArray.filter((_, i) => i !== index);
    setRfq((prev) => ({ ...prev, currencies: updated }));
  };

  const handleNext = async () => {
    if (currencyArray.length === 0) {
      alert("Please add at least one currency.");
      return;
    }
    if (!rfq.origin) {
      alert("Please select an Origin.");
      return;
    }
    if (!rfq.destination) {
      alert("Please select a Destination.");
      return;
    }

    setBtnLoading(true);

    const payload = {
      logisticDraftId: rfq.logisticDraftId,
      username: storedUser.username,
      currencies: currencyArray,
      origin: rfq.origin,
      destination: rfq.destination,
      modeOfConsignment: rfq.modeOfConsignment,
      consignee: rfq.consignee,
      invoiceNo: rfq.invoiceNo,
      transitTime: rfq.transitTime,
      noOfPallets: rfq.noOfPallets,
      grossWeight: rfq.grossWeight,
      length: rfq.length,
      breadth: rfq.breadth,
      height: rfq.height,
      volumeCBM: rfq.volumeCBM,
      address: rfq.address,
      status: rfq.status,
      submissionDate: rfq.submissionDate
    };

    try {
      const res = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/logistic-draft`,
        payload,
        { headers: { Authorization: `Bearer ${storedUser.accessToken}` } }
      );
      if (res.data) {
        setRfq((prev) => ({
          ...prev,
          ...res.data,
          currencies: res.data.currencies || currencyArray
        }));
      }
      setStep(3);
    } catch (error) {
      console.error("Step2 save error:", error);
      const backendMessage = error.response?.data;
      alert(`Error saving Step 2: ${
        typeof backendMessage === "string"
          ? backendMessage
          : backendMessage?.message || "Check backend connection."
      }`);
    } finally {
      setBtnLoading(false);
    }
  };

  const autocompleteStyle = {
    marginTop: "5px",
    "& .MuiOutlinedInput-root": {
      borderRadius: "7px",
      minHeight: "48px",
      padding: "0 !important",
      fontFamily: "Inter !important",
      "& fieldset": { borderColor: "#c7c7c7" },
      "&:hover fieldset": { borderColor: "#D8E9F0" },
      "&.Mui-focused fieldset": { borderColor: "#1a71f6", borderWidth: "1px" }
    },
    "& .MuiOutlinedInput-input": { padding: "12px 15px !important" },
  };

  return (
    <div className="dashboard-container">
      <Typography
        variant="h6"
        className="form-title"
        sx={{ paddingLeft: "20px", fontWeight: "bold" }}
      >
        Step 2: Financial & Routing
      </Typography>

      <div style={{ width: "100%", padding: "20px" }}>
        <Grid2 container spacing={3} alignItems="flex-end">

          <Grid2 size={{ xs: 12, sm: 5 }}>
            <label>Select Currency*</label>
            <Autocomplete
              options={CURRENCIES}
              value={selectedCurr}
              onChange={(e, val) => setSelectedCurr(val)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="Select Currency"
                  variant="outlined"
                  size="small"
                  sx={autocompleteStyle}
                />
              )}
              slotProps={{
                paper: {
                  sx: {
                    background: "#D8E9F0",
                    "& .MuiAutocomplete-option": {
                      fontSize: "13px",
                      fontFamily: "Inter",
                      borderBottom: "1px solid #ffffff",
                    },
                  },
                },
              }}
            />
          </Grid2>

          <Grid2 size={{ xs: 12, sm: 4 }}>
            <label>Rate*</label>
            <input
              type="number"
              placeholder="Enter Rate"
              value={selectedRate}
              onChange={(e) => setSelectedRate(e.target.value)}
              style={{ marginBottom: "0px", marginTop: "5px", height: "48px" }}
            />
          </Grid2>

          <Grid2 size={{ xs: 12, sm: 3 }}>
            <Button
              className="btn-contained"
              onClick={addCurrencyRate}
              sx={{ width: "100%" }}
            >
              Add Currency
            </Button>
          </Grid2>

          <Grid2 size={{ xs: 12 }}>
            <TableContainer
              component={Paper}
              className="data-grid"
              sx={{ boxShadow: "none", overflow: "hidden" }}
            >
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell className="table-title">Currency Name</TableCell>
                    <TableCell className="table-title">Exchange Rate</TableCell>
                    <TableCell className="table-title" align="center">Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {currencyArray.length > 0 ? (
                    currencyArray.map((item, index) => (
                      <TableRow key={index}>
                        <TableCell>{item.name}</TableCell>
                        <TableCell>{item.rate}</TableCell>
                        <TableCell align="center">
                          <IconButton
                            color="error"
                            onClick={() => removeCurrencyRate(index)}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell
                        colSpan={3}
                        align="center"
                        sx={{ py: 3, color: "#737373" }}
                      >
                        No currencies added. Select a currency and rate above.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Grid2>

          <Grid2 size={{ xs: 12, sm: 6 }}>
            <label>Origin *</label>
            <Autocomplete
              options={origins}
              value={rfq.origin || null}
              onChange={(e, val) => setRfq({ ...rfq, origin: val })}
              renderInput={(params) => (
                <TextField
                  {...params}
                  variant="outlined"
                  size="small"
                  sx={autocompleteStyle}
                />
              )}
              slotProps={{
                paper: {
                  sx: {
                    background: "#D8E9F0",
                    "& .MuiAutocomplete-option": {
                      fontSize: "13px",
                      fontFamily: "Inter",
                      borderBottom: "1px solid #ffffff",
                    },
                  },
                },
              }}
            />
          </Grid2>

          <Grid2 size={{ xs: 12, sm: 6 }}>
            <label>Destination *</label>
            <Autocomplete
              options={destinations}
              value={rfq.destination || null}
              onChange={(e, val) => setRfq({ ...rfq, destination: val })}
              renderInput={(params) => (
                <TextField
                  {...params}
                  variant="outlined"
                  size="small"
                  sx={autocompleteStyle}
                />
              )}
              slotProps={{
                paper: {
                  sx: {
                    background: "#D8E9F0",
                    "& .MuiAutocomplete-option": {
                      fontSize: "13px",
                      fontFamily: "Inter",
                      borderBottom: "1px solid #ffffff",
                    },
                  },
                },
              }}
            />
          </Grid2>

        </Grid2>

        <Box className="btn-container" sx={{ gap: 3 }}>
          <Button
            className="btn-outlined"
            onClick={() => setStep(1)}
          >
            Back
          </Button>
          <Button
            className="btn-contained"
            onClick={handleNext}
            disabled={btnLoading || currencyArray.length === 0}
          >
            {btnLoading ? "Saving..." : "Next Step"}
          </Button>
        </Box>
      </div>
    </div>
  );
};

export default Step2;
